import { Action, createAction, props } from '@ngrx/store'
import {
  AssignEventTicketPayload,
  BaseEventTicketEntity,
  CheckInGuestResultEnum,
  EditEventTicketPayload,
  EventGuestSummary,
  EventTicketFilters,
  TransferEventTicketPayload,
} from './base-event-tickets.models'
import { HttpErrorResponse } from '@angular/common/http'
import { EventGuestScannerViewEnum } from 'libs/shared/event-tickets/feature/src/lib/gb-event-guest-scanner-dialog/gb-event-guest-scanner-view.service'
import { Update } from '@ngrx/entity'

export const loadEventTickets = createAction('[EventTickets/Page] Load EventTickets')

export const loadFilteredEventTickets = createAction(
  '[EventTickets/Page] Load Filtered EventTickets',
  (filters?: EventTicketFilters) => ({ filters }),
)

export const loadEventTicketsSuccess = createAction(
  '[EventTickets/API] Load EventTickets Success',
  props<{ eventTickets: BaseEventTicketEntity[]; count: number }>(),
)

export const loadEventTicketsFailure = createAction(
  '[EventTickets/API] Load EventTickets Failure',
  props<{ error: HttpErrorResponse }>(),
)

export const syncEventTicket = createAction(
  '[EventTickets/Page] Sync Event Ticket',
  props<{
    uuid: string
    token?: string
    showLoader?: boolean
    onSuccess?: () => Action
    onError?: (error: HttpErrorResponse) => Action
  }>(),
)

export const syncEventTicketSuccess = createAction(
  '[EventTickets/API] Sync Event Ticket Success',
  props<{ eventTicket: BaseEventTicketEntity }>(),
)

export const syncEventTicketFailure = createAction(
  '[EventTickets/API] Sync Event Ticket Failure',
  props<{ error: HttpErrorResponse }>(),
)

export const updateSelectedEventTicket = createAction(
  '[EventTickets/Page] Update Selected Event Ticket',
  props<{ uuid: string }>(),
)

export const loadEventGuestSummary = createAction('[EventTickets/Page] Load Event Guest Summary')

export const loadEventGuestSummarySuccess = createAction(
  '[EventTickets/API] Load Event Guest Summary Success',
  props<{ summary: any }>(),
)

export const loadEventGuestSummaryFailure = createAction(
  '[EventTickets/API] Load Event Guest Summary Failure',
  props<{ error: HttpErrorResponse }>(),
)

export const updateEventGuestSummaryRealtime = createAction(
  '[EventTickets/API] Update Event Guest Summary Realtime',
  props<{ update: Update<EventGuestSummary> }>(),
)

export const upsertEventTicketOnSuccess = createAction(
  '[EventTickets/Page] Upsert Event Ticket On Success',
  props<{ eventTicket: BaseEventTicketEntity }>(),
)

export const upsertEventTicket = createAction(
  '[EventTickets/Page] Upsert Event Ticket',
  props<{ eventTicket: BaseEventTicketEntity; oldEventTicket?: BaseEventTicketEntity }>(),
)

export const initEventTicketsPage = createAction(
  '[EventTickets/Page] Init Event Tickets Page',
  props<{ filters: EventTicketFilters }>(),
)

// ASSIGN ACTIONS

export const assignEventTicket = createAction(
  '[EventTickets/API] Assign Event Ticket',
  props<{ payload: AssignEventTicketPayload }>(),
)

export const assignEventTicketSuccess = createAction(
  '[EventTickets/API] Assign Event Ticket Success',
  props<{ eventTicket: BaseEventTicketEntity }>(),
)

export const assignEventTicketFailure = createAction(
  '[EventTickets/API] Assign Event Ticket Failure',
  props<{ error: HttpErrorResponse }>(),
)

export const bulkAssignEventTickets = createAction(
  '[EventTickets/API] Bulk Assign Event Tickets',
  props<{ payload: AssignEventTicketPayload[] }>(),
)

export const bulkAssignEventTicketsSuccess = createAction('[EventTickets/API] Bulk Assign Event Tickets Success')

export const bulkAssignEventTicketsFailure = createAction(
  '[EventTickets/API] Bulk Assign Event Tickets Failure',
  props<{ error: HttpErrorResponse }>(),
)

export const transferEventTicket = createAction(
  '[EventTickets/API] Transfer Event Ticket',
  props<{ payload: TransferEventTicketPayload }>(),
)

export const transferEventTicketSuccess = createAction(
  '[EventTickets/API] Transfer Event Ticket Success',
  props<{
    eventTicket: BaseEventTicketEntity
    oldEventTicket: BaseEventTicketEntity
  }>(),
)

export const transferEventTicketFailure = createAction(
  '[EventTickets/API] Transfer Event Ticket Failure',
  props<{ error: HttpErrorResponse }>(),
)

// EDIT ACTIONS

export const editEventTicket = createAction(
  '[EventTickets/API] Edit Event Ticket',
  props<{ payload: EditEventTicketPayload; isCheckIn?: boolean; onSuccess?: () => void }>(),
)

export const editEventTicketSuccess = createAction(
  '[EventTickets/API] Edit Event Ticket Success',
  props<{ eventTicket: BaseEventTicketEntity; isCheckIn?: boolean; onSuccess?: () => void }>(),
)

export const editEventTicketFailure = createAction(
  '[EventTickets/API] Edit Event Ticket Failure',
  props<{ error: HttpErrorResponse }>(),
)

// SEND ACTIONS

export const sendEventTicket = createAction('[EventTickets/API] Send Event Ticket')

export const sendEventTicketSuccess = createAction('[EventTickets/API] Send Event Ticket Success')

export const sendEventTicketFailure = createAction(
  '[EventTickets/API] Send Event Ticket Failure',
  props<{ error: HttpErrorResponse }>(),
)

// CHECK-IN ACTIONS

export const startQRCodeCheckInProcess = createAction(
  '[EventTickets/Page] Start QR Code Check In Process',
  props<{ uuid: string }>(),
)

export const startCheckInProcess = createAction(
  '[EventTickets/Page] Start Check In Process',
  props<{ uuid: string; isQRCodeCheckIn?: boolean; onComplete?: (result?: CheckInGuestResultEnum) => Action }>(),
)

export const checkInGuest = createAction(
  '[EventTickets/API] Check In Guest',
  props<{ uuid: string; isQRCodeCheckIn?: boolean; onComplete?: (result?: CheckInGuestResultEnum) => Action }>(),
)

export const checkInGuestSuccess = createAction(
  '[EventTickets/API] Check In Guest Success',
  props<{
    eventTicket: BaseEventTicketEntity
    uuid: string
    isQRCodeCheckIn?: boolean
    onComplete?: (result?: CheckInGuestResultEnum) => Action
  }>(),
)

export const checkInGuestFailure = createAction(
  '[EventTickets/API] Check In Guest Failure',
  props<{
    error: HttpErrorResponse
    uuid: string
    isQRCodeCheckIn?: boolean
    onComplete?: (result?: CheckInGuestResultEnum) => Action
  }>(),
)

export const checkOutGuest = createAction(
  '[EventTickets/API] Check Out Guest',
  props<{ uuid: string; onComplete?: () => void }>(),
)

export const checkOutGuestSuccess = createAction(
  '[EventTickets/API] Check Out Guest Success',
  props<{ eventTicket: BaseEventTicketEntity; uuid: string }>(),
)

export const checkOutGuestFailure = createAction(
  '[EventTickets/API] Check Out Guest Failure',
  props<{ error: HttpErrorResponse; uuid: string }>(),
)

// DIALOG ACTIONS

export const openEventGuestScannerDialog = createAction(
  '[EventTickets/Page] Open Event Guest Scanner Dialog',
  (view = EventGuestScannerViewEnum.Start, result?: CheckInGuestResultEnum) => ({ view, result }),
)

export const openEventTicketAssignDialog = createAction(
  '[EventTickets/Page] Open Event Ticket Assign Dialog',
  props<{ uuid: string }>(),
)

export const openEventTicketEditGuestDialog = createAction(
  '[EventTickets/Page] Open Event Ticket Edit Guest Dialog',
  props<{ uuid: string; isCheckIn?: boolean; onSuccess?: () => Action }>(),
)

export const openResendInviteDialog = createAction(
  '[EventTickets/Page] Open Resend Invite Dialog',
  props<{ uuid: string }>(),
)

export const openResendTicketDialog = createAction(
  '[EventTickets/Page] Open Resend Ticket Dialog',
  props<{ uuid: string }>(),
)

export const openEventTicketTransferDialog = createAction(
  '[EventTickets/Page] Open Event Ticket Transfer Dialog',
  props<{ uuid: string }>(),
)

// PURCHASES ACTIONS

export const syncEventTicketPurchase = createAction(
  '[EventTicketPurchases/API] Sync Event Ticket Purchase',
  props<{ uuid: string }>(),
)

export const finaliseEventTicketPurchaseSuccess = createAction(
  '[EventTicketPurchases/API] Finalise Event Ticket Purchase Success',
)

// PAGINATION ACTIONS

export const setPaginationSettings = createAction(
  '[EventTickets/Page] Set Pagination Settings',
  props<{ paginationPreferences: { limit: number; offset: number } }>(),
)

// CLEAR ACTIONS

export const clearSelectedEventTicket = createAction('[EventTickets/Page] Clear Selected Event Ticket')

export const removeEventTicket = createAction('[EventTickets/Page] Remove Event Ticket', props<{ uuid: string }>())

export const removeNotListLoadedEventTickets = createAction('[EventTickets/Page] Remove Not List Loaded Event Tickets')

export const removeManyEventTickets = createAction(
  '[EventTickets/Page] Remove Many Event Tickets',
  props<{ uuids: string[] }>(),
)

export const removeAllEventTickets = createAction('[EventTickets/Page] Remove All Event Tickets')

export const clearEventTickets = createAction('[EventTickets/Page] Clear Event Tickets')
